import {isApiError} from '@shared/helpers/api'
import {includes} from '@shared/helpers/collection'

import {
    CouponFailReasons,
    DeliveryFailReasons,
    DeliveryNotOnSaleReasons,
    NotOnSaleReasons,
    OrderAlreadyCreatedReasons,
    OutOfStockReasons,
    ReasonType,
} from '@web/core/constants'
import {isBFFApiError} from '@web/helpers/api'

import {ItemValidationFailure, OrderApiFailure, OrderProcessResult} from '../apis/interface'
import {ErrorMessage, FinishStatusCodeType, FinishStatusCode, FinishCode, DetailCauseCode} from '../constants'
import {FinishStatus} from '../interfaces'

/** 성공으로 종료 */
export function getSuccessFinished(): FinishStatus {
    return {
        code: FinishStatusCode.COMPLETED,
        message: '성공',
    }
}

/** 알수 없는 이유로 종료 */
export function getUnknownFinished(message?: string): FinishStatus {
    return {
        code: FinishStatusCode.UNKNOWN,
        message: message ?? '알수 없는 payload',
    }
}

/** 처리할 수 없는 코스로 종료 */
export function getNotFoundCourseFinished(): FinishStatus {
    return {
        code: FinishStatusCode.UNKNOWN,
        message: '처리할 수 없는 Course 입니다.',
    }
}

/** 처리할 수 없는 코스로 종료 */
export function getEmptyOrderRequestFinished(): FinishStatus {
    return {
        code: FinishStatusCode.UNKNOWN,
        message: 'OrderRequest 의 정보가 없습니다',
    }
}

type ErrorCodeCase = 'API_FAIL' | 'AXIOS_FAIL' | 'AXIOS_UNKNOWN_FAIL' | 'UNKNOWN_FAIL'

function getFailDetail(code: FinishStatusCodeType, codeCase: ErrorCodeCase) {
    const failMap: Partial<Record<FinishStatusCodeType, Record<ErrorCodeCase, FinishStatusCodeType>>> = {
        [FinishCode.PREPARE_FAIL]: {
            API_FAIL: DetailCauseCode.PREPARE_API_FAIL,
            AXIOS_FAIL: DetailCauseCode.PREPARE_AXIOS_FAIL,
            AXIOS_UNKNOWN_FAIL: DetailCauseCode.PREPARE_AXIOS_UNKNOWN_FAIL,
            UNKNOWN_FAIL: DetailCauseCode.PREPARE_UNKNOWN_FAIL,
        },
        [FinishCode.PROCESSING_FAILURE]: {
            API_FAIL: DetailCauseCode.PROCESSING_API_FAIL,
            AXIOS_FAIL: DetailCauseCode.PROCESSING_AXIOS_FAIL,
            AXIOS_UNKNOWN_FAIL: DetailCauseCode.PROCESSING_AXIOS_UNKNOWN_FAIL,
            UNKNOWN_FAIL: DetailCauseCode.PROCESSING_UNKNOWN_FAIL,
        },
        [FinishCode.APPLY_FAILURE]: {
            API_FAIL: DetailCauseCode.APPLY_API_FAIL,
            AXIOS_FAIL: DetailCauseCode.APPLY_AXIOS_FAIL,
            AXIOS_UNKNOWN_FAIL: DetailCauseCode.APPLY_AXIOS_UNKNOWN_FAIL,
            UNKNOWN_FAIL: DetailCauseCode.APPLY_UNKNOWN_FAIL,
        },
    }
    return failMap[code]?.[codeCase] ?? code
}

/** api 호출중 에러로 종료발생 */
export function getApiErrorFinished(code: FinishStatusCodeType, e: unknown): FinishStatus {
    if (isBFFApiError<OrderApiFailure>(e)) {
        const reasonType: FinishStatusCodeType = (function () {
            if (e.result?.validationFailure?.reasonType) {
                return e.result?.validationFailure?.reasonType
            }
            if (e.result?.code === ReasonType.ORDER_PROCESS_ALREADY_STARTED) {
                return ReasonType.ORDER_PROCESS_ALREADY_STARTED
            }
            if (e.result?.code) {
                return e.result?.code
            }
            return getFailDetail(code, 'API_FAIL')
        })()
        return {
            code: reasonType,
            message: e.result?.fallbackMessage ?? ErrorMessage.OrderApiFail,
            data: e.result,
        }
    }
    if (isApiError(e)) {
        if (e.response?.data.result) {
            const reasonType: FinishStatusCodeType = (function () {
                if (e.response.data?.validationFailure?.reasonType) {
                    return e.response.data?.validationFailure?.reasonType
                }
                if (e.response.data?.result?.validationFailure?.reasonType) {
                    return e.response.data?.result?.validationFailure?.reasonType
                }
                // {
                //     "detail": "OrderProcess request is failed.",
                //     "fallbackMessage": "잠시 후 결제가 완료되니 결제정보를 확인해 주시기 바랍니다.",
                //     "code": "ORDER_PROCESS_ALREADY_STARTED",
                //     "orderProcessId": "2024030633647600",
                //     "orderSheetId": "f7d745b5-afdd-81fd-7205-016399ed3e40"
                // }
                if (e.response.data?.result?.code === ReasonType.ORDER_PROCESS_ALREADY_STARTED) {
                    return ReasonType.ORDER_PROCESS_ALREADY_STARTED
                }
                if (e.response.data?.result?.code) {
                    return e.response.data?.result?.code
                }
                return getFailDetail(code, 'AXIOS_FAIL')
            })()
            return {
                code: reasonType,
                message: e.response?.data.result.fallbackMessage ?? ErrorMessage.OrderApiFail,
                data: e.response?.data.result,
                ...(function () {
                    if (reasonType === ReasonType.ORDER_PROCESS_ALREADY_STARTED) {
                        return {
                            content: {
                                state: 'PAYMENT_FAILED',
                                reason: ReasonType.ORDER_PROCESS_ALREADY_STARTED,
                                failure: {
                                    orderProcessId: e.response.data?.result?.orderProcessId,
                                    orderSheetId: e.response.data?.result?.orderSheetId,
                                },
                            },
                        }
                    }
                    if (reasonType === ReasonType.EXCEED_FOREIGNER_ORDER_PAY_AMOUNT) {
                        const remainAmount =
                            e.response?.data?.result.validationFailure?.reasonAttributes?.remainAmount ?? 0
                        return {
                            content: {
                                state: 'ORDER_COMPLETED',
                                reason: 'EXCEED_FOREIGNER_ORDER_PAY_AMOUNT',
                                failure: {
                                    remainAmount,
                                },
                            },
                        }
                    }
                    if (reasonType === ReasonType.INVALID_ORDER_STATUS) {
                        const orderStatusType =
                            e.response?.data?.result.validationFailure?.reasonAttributes?.orderStatusType ?? ''
                        // ORDERING
                        // ORDER_COMPLETED
                        // PAYING
                        // ORDER_FAILED
                        // APPROVAL_FAILED
                        // ERROR
                        // PAYMENT_ERROR
                        // CANCELED_BY_NOPAYMENT
                        // CANCELED
                        // PAYED
                        if (orderStatusType === 'PAYED') {
                            return {
                                content: {
                                    state: 'ORDER_COMPLETED',
                                    reason: 'PAYED',
                                    failure: e.response?.data.result.validationFailure,
                                },
                            }
                        }
                        if (orderStatusType === 'CANCELED_BY_NOPAYMENT') {
                            return {
                                content: {
                                    state: 'ORDER_COMPLETED',
                                    reason: 'CANCELED_BY_NOPAYMENT',
                                    failure: e.response?.data.result.validationFailure,
                                },
                            }
                        }
                    }
                    if (reasonType === ReasonType.ORDER_ALREADY_CREATED) {
                        return {
                            content: {
                                state: 'ORDER_COMPLETED',
                                reason: 'ORDER_ALREADY_CREATED',
                                failure: e.response?.data.result.validationFailure,
                            },
                        }
                    }

                    return {}
                })(),
            }
        }
        return {
            code: getFailDetail(code, 'AXIOS_UNKNOWN_FAIL'),
            message: e.response?.data.message ?? ErrorMessage.OrderApiFail,
        }
    }
    return {
        code: getFailDetail(code, 'UNKNOWN_FAIL'),
        message: ErrorMessage.OrderApiFail,
    }
}

/** process 에서 에러 검출로 종료발생 */
export function getOrderProcessErrorFinished(code: FinishStatusCodeType, data: OrderProcessResult): FinishStatus {
    const {
        orderProcessFailure,
        orderProcessValidationFailure,
        applyOrderProcessFailure,
        applyOrderProcessValidationFailure,
    } = data
    if (applyOrderProcessValidationFailure || applyOrderProcessFailure) {
        const fallbackMessage =
            (applyOrderProcessValidationFailure || applyOrderProcessFailure)?.fallbackMessage ??
            ErrorMessage.OrderApiFail
        const reasonType = applyOrderProcessValidationFailure?.reasonType

        const includesReason = includes(reasonType)
        if (reasonType && includesReason(OrderAlreadyCreatedReasons)) {
            return {
                code: reasonType,
                message: '이미 완료된 주문',
                content: {
                    reason: ReasonType.ORDER_ALREADY_CREATED,
                    state: data.state,
                    failure: applyOrderProcessValidationFailure,
                },
            }
        }

        return {
            code: reasonType ?? code,
            message: fallbackMessage,
        }
    }
    if (orderProcessValidationFailure) {
        const {reasonType, reasonAttributes} = orderProcessValidationFailure
        const includesReason = includes(reasonType)

        if (OutOfStockReasons.includes(reasonType)) {
            return {
                code: reasonType,
                message: '재고 부족등 구매 불가 상품 안내 케이스',
                content: {
                    reason: ReasonType.OUT_OF_STOCK,
                    state: data.state,
                    failure: orderProcessValidationFailure as ItemValidationFailure,
                },
            }
        } else if (NotOnSaleReasons.includes(reasonType)) {
            return {
                code: reasonType,
                message: '구매 불가 상품 안내 케이스',
                content: {
                    reason: ReasonType.PRODUCT_STATUS,
                    state: data.state,
                    failure: orderProcessValidationFailure as ItemValidationFailure,
                },
            }
        } else if (DeliveryNotOnSaleReasons.includes(reasonType) || DeliveryFailReasons.includes(reasonType)) {
            return {
                code: reasonType,
                message: '구매 불가 상품 안내 케이스',
                content: {
                    reason: ReasonType.PRODUCT_STATUS,
                    state: data.state,
                    failure: orderProcessValidationFailure as ItemValidationFailure,
                },
            }
        } else if (reasonType === ReasonType.BLACK_CONSUMER || reasonType === ReasonType.BLACKCONSUMER) {
            return {
                code: FinishStatusCode.BLACK_CONSUMER,
                message: '블랙컨슈머',
                content: {
                    reason: ReasonType.BLACK_CONSUMER,
                    state: data.state,
                    failure: orderProcessValidationFailure,
                },
            }
        } else if (includesReason(OrderAlreadyCreatedReasons)) {
            return {
                code: reasonType,
                message: '이미 완료된 주문',
                content: {
                    reason: ReasonType.ORDER_ALREADY_CREATED,
                    state: data.state,
                    failure: orderProcessValidationFailure,
                },
            }
        } else if (CouponFailReasons.includes(reasonType)) {
            return {
                code: reasonType,
                message: '사용할 수 없는 쿠폰입니다. 주문서를 새로고침 하거나 다시 진입해주세요.',
            }
        } else if (reasonType === ReasonType.EXCEED_FOREIGNER_ORDER_PAY_AMOUNT) {
            return {
                code: reasonType,
                message:
                    '월 결제한도 500만원 금액 초과로 결제가 불가능합니다. 이번 달 추가 결제 가능 금액은 {{remainAmount}}원 입니다.',
                content: {
                    state: data.state,
                    reason: 'EXCEED_FOREIGNER_ORDER_PAY_AMOUNT',
                    failure: {
                        remainAmount: (reasonAttributes?.remainAmount as number) ?? 0,
                    },
                },
            }
        }
    }

    const fallbackMessage =
        (orderProcessFailure || orderProcessValidationFailure)?.fallbackMessage ?? ErrorMessage.OrderApiFail
    const reasonType = orderProcessValidationFailure?.reasonType ?? undefined
    return {
        code: reasonType ?? code,
        message: fallbackMessage,
    }
}
